import React, { useState, useEffect, useContext } from 'react';
import { Link, Navigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { toast } from 'sonner';
import {
  Users, Trophy, Activity, MessageSquare, Wrench, BarChart3,
  UserPlus, Trophy as TrophyIcon, UserCheck, TrendingUp, AlertTriangle,
  Send, Megaphone, ChevronRight, Calendar, Award, Eye, RefreshCw,
  CheckCircle2, ArrowRight, Search, GraduationCap, Bell
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StatCard = ({ icon: Icon, label, value, gradient, hint, testId }) => (
  <div className={`relative overflow-hidden rounded-2xl p-5 bg-gradient-to-br ${gradient} text-white shadow-xl hover:-translate-y-1 transition-all`} data-testid={testId}>
    <div className="absolute -top-8 -right-8 w-28 h-28 bg-white/15 rounded-full blur-2xl"></div>
    <div className="relative">
      <Icon className="w-8 h-8 mb-2 opacity-90" />
      <div className="text-3xl font-black tracking-tight">{value}</div>
      <div className="text-sm font-medium opacity-90">{label}</div>
      {hint && <div className="text-xs mt-1 opacity-75">{hint}</div>}
    </div>
  </div>
);

const ActivityIcon = ({ type }) => {
  const map = {
    user_registered: { Icon: UserPlus, bg: 'bg-blue-100', color: 'text-blue-600' },
    tournament_completed: { Icon: TrophyIcon, bg: 'bg-amber-100', color: 'text-amber-600' },
    coach_pending: { Icon: UserCheck, bg: 'bg-purple-100', color: 'text-purple-600' },
  };
  const e = map[type] || map.user_registered;
  return (
    <div className={`w-10 h-10 rounded-xl ${e.bg} flex items-center justify-center shrink-0`}>
      <e.Icon className={`w-5 h-5 ${e.color}`} />
    </div>
  );
};

const AdminControlPanel = () => {
  const { user } = useContext(AuthContext);
  const [dashboard, setDashboard] = useState(null);
  const [activity, setActivity] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [usersSearch, setUsersSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('overview');
  const [broadcastForm, setBroadcastForm] = useState({ title: '', body: '' });
  const [pushForm, setPushForm] = useState({ title: '', body: '', link: '', target: 'all', skill_level: '', tournament_id: '', tournament_audience: 'registered' });
  const [pushStats, setPushStats] = useState(null);
  const [sendingPush, setSendingPush] = useState(false);
  const [tools, setTools] = useState({ recompute: false, autoComplete: false });
  const [incompleteUsers, setIncompleteUsers] = useState([]);

  const isAdmin = user && (user.is_admin || ['shafirroy@icloud.com', 'shafirroy@gmail.com'].includes(user.email));

  useEffect(() => {
    if (!isAdmin) return;
    loadAll();
    fetchPushStats();
    // eslint-disable-next-line
  }, [isAdmin]);

  const loadAll = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const [d, a, u, ic] = await Promise.all([
        axios.get(`${API}/admin/dashboard`, { headers }),
        axios.get(`${API}/admin/activity-feed`, { headers }),
        axios.get(`${API}/admin/users`, { headers }),
        axios.get(`${API}/admin/users-incomplete`, { headers }).catch(() => ({ data: [] })),
      ]);
      setDashboard(d.data);
      setActivity(a.data.activity || []);
      setAllUsers(Array.isArray(u.data) ? u.data : (u.data?.users || []));
      setIncompleteUsers(Array.isArray(ic.data) ? ic.data : (ic.data?.users || []));
    } catch (e) {
      toast.error('שגיאה בטעינת לוח האדמין');
    } finally {
      setLoading(false);
    }
  };

  const handleRecompute = async () => {
    if (!window.confirm('לחשב מחדש סטטיסטיקות לכל השחקנים?')) return;
    setTools(s => ({ ...s, recompute: true }));
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const r = await axios.post(`${API}/admin/recompute-all-stats`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`עודכנו ${r.data.details?.users_updated || 0} שחקנים`);
      loadAll();
    } catch { toast.error('שגיאה'); }
    finally { setTools(s => ({ ...s, recompute: false })); }
  };

  const handleAutoComplete = async () => {
    if (!window.confirm('לסמן את כל התחרויות שהסתיימו?')) return;
    setTools(s => ({ ...s, autoComplete: true }));
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const r = await axios.post(`${API}/admin/tournaments/auto-complete-finished`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`${(r.data.completed || []).length} תחרויות סומנו`);
      loadAll();
    } catch { toast.error('שגיאה'); }
    finally { setTools(s => ({ ...s, autoComplete: false })); }
  };

  const handleBroadcast = async () => {
    if (!broadcastForm.title.trim() || !broadcastForm.body.trim()) {
      toast.error('כותרת ותוכן נדרשים');
      return;
    }
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const r = await axios.post(`${API}/admin/broadcast-message`, broadcastForm, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(r.data.message);
      setBroadcastForm({ title: '', body: '' });
    } catch { toast.error('שגיאה בשליחת הודעה'); }
  };

  const fetchPushStats = async () => {
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const r = await axios.get(`${API}/admin/push-stats`, { headers: { Authorization: `Bearer ${token}` } });
      setPushStats(r.data);
    } catch { /* ignore */ }
  };

  const handleSendPush = async () => {
    if (!pushForm.title.trim() || !pushForm.body.trim()) {
      toast.error('כותרת ותוכן נדרשים');
      return;
    }
    if (pushForm.target === 'skill_level' && !pushForm.skill_level) {
      toast.error('יש לבחור דרג');
      return;
    }
    if (pushForm.target === 'tournament' && !pushForm.tournament_id) {
      toast.error('יש לבחור תחרות');
      return;
    }
    setSendingPush(true);
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const r = await axios.post(`${API}/admin/send-push`, pushForm, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`${r.data.message} · ${r.data.device_subscriptions} מכשירים רשומים`, { duration: 6000 });
      setPushForm({ title: '', body: '', link: '', target: 'all', skill_level: '', tournament_id: '', tournament_audience: 'registered' });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'שגיאה בשליחת ההתראה');
    } finally {
      setSendingPush(false);
    }
  };

  if (!isAdmin) return <Navigate to="/" replace />;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">טוען לוח אדמין...</p>
        </div>
      </div>
    );
  }

  const stats = dashboard?.stats || {};
  const filteredUsers = allUsers.filter(u =>
    !usersSearch ||
    (u.full_name || '').toLowerCase().includes(usersSearch.toLowerCase()) ||
    (u.email || '').toLowerCase().includes(usersSearch.toLowerCase())
  );

  // Mini chart data
  const chart = dashboard?.registrations_chart || [];
  const maxCount = Math.max(...chart.map(c => c.count), 1);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-emerald-50/30 pb-20">
      {/* Header */}
      <div className="relative overflow-hidden bg-gradient-to-br from-slate-900 via-blue-900 to-emerald-900 text-white">
        <div className="absolute -top-20 -right-20 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-20 -left-20 w-96 h-96 bg-emerald-500/20 rounded-full blur-3xl"></div>
        <div className="relative max-w-7xl mx-auto px-4 py-8 md:py-12">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 backdrop-blur border border-white/20 mb-3">
                <Activity className="w-4 h-4 text-emerald-300" />
                <span className="text-sm font-bold text-emerald-200">מערכת ניהול</span>
              </div>
              <h1 className="text-3xl md:text-5xl font-black tracking-tight">לוח בקרה ראשי</h1>
              <p className="text-blue-200 mt-2">כל מה שצריך לדעת ולעשות באתר - במקום אחד</p>
            </div>
            <Button onClick={loadAll} className="bg-white/10 backdrop-blur border border-white/20 hover:bg-white/20 text-white" data-testid="refresh-dashboard-btn">
              <RefreshCw className="w-4 h-4 ml-2" />רענן
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 -mt-6 relative z-10">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="grid grid-cols-3 md:grid-cols-6 gap-1 bg-white shadow-xl rounded-2xl p-1 h-auto" data-testid="admin-tabs">
            <TabsTrigger value="overview" className="data-[state=active]:bg-gradient-to-br data-[state=active]:from-blue-500 data-[state=active]:to-cyan-500 data-[state=active]:text-white rounded-xl py-3"><BarChart3 className="w-4 h-4 ml-1.5" />סקירה</TabsTrigger>
            <TabsTrigger value="users" className="data-[state=active]:bg-gradient-to-br data-[state=active]:from-emerald-500 data-[state=active]:to-teal-500 data-[state=active]:text-white rounded-xl py-3"><Users className="w-4 h-4 ml-1.5" />משתמשים</TabsTrigger>
            <TabsTrigger value="activity" className="data-[state=active]:bg-gradient-to-br data-[state=active]:from-purple-500 data-[state=active]:to-indigo-500 data-[state=active]:text-white rounded-xl py-3"><Activity className="w-4 h-4 ml-1.5" />פעילות</TabsTrigger>
            <TabsTrigger value="tournaments" className="data-[state=active]:bg-gradient-to-br data-[state=active]:from-amber-500 data-[state=active]:to-orange-500 data-[state=active]:text-white rounded-xl py-3"><Trophy className="w-4 h-4 ml-1.5" />תחרויות</TabsTrigger>
            <TabsTrigger value="messages" className="data-[state=active]:bg-gradient-to-br data-[state=active]:from-pink-500 data-[state=active]:to-rose-500 data-[state=active]:text-white rounded-xl py-3"><MessageSquare className="w-4 h-4 ml-1.5" />הודעות</TabsTrigger>
            <TabsTrigger value="tools" className="data-[state=active]:bg-gradient-to-br data-[state=active]:from-slate-700 data-[state=active]:to-slate-900 data-[state=active]:text-white rounded-xl py-3"><Wrench className="w-4 h-4 ml-1.5" />כלים</TabsTrigger>
          </TabsList>

          {/* ============== TAB: Overview ============== */}
          <TabsContent value="overview" className="mt-6 space-y-6">
            {/* KPI Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard icon={Users} value={stats.total_users || 0} label="שחקנים רשומים" gradient="from-blue-600 to-cyan-600" hint={`+${stats.new_users_this_week || 0} השבוע`} testId="stat-total-users" />
              <StatCard icon={Trophy} value={stats.active_tournaments || 0} label="תחרויות פעילות" gradient="from-amber-500 to-orange-600" hint={`${stats.completed_tournaments || 0} הסתיימו`} testId="stat-active-tournaments" />
              <StatCard icon={GraduationCap} value={stats.total_coaches || 0} label="מאמנים פעילים" gradient="from-emerald-500 to-teal-600" hint={stats.pending_coaches > 0 ? `${stats.pending_coaches} ממתינים` : 'הכל מאושר'} testId="stat-coaches" />
              <StatCard icon={TrendingUp} value={stats.matches_this_week || 0} label="משחקים השבוע" gradient="from-purple-500 to-indigo-600" hint={`${stats.total_matches || 0} סה"כ`} testId="stat-matches-week" />
            </div>

            {/* Alerts */}
            {(stats.pending_coaches > 0 || stats.unread_messages > 0 || stats.pending_cancellations > 0) && (
              <Card className="border-amber-200 bg-amber-50/50">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <AlertTriangle className="w-5 h-5 text-amber-600" />
                    <span className="font-bold text-amber-900">דורש תשומת לב</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {stats.pending_coaches > 0 && (
                      <Link to="/admin/coaches" className="px-3 py-1.5 bg-white rounded-lg border border-amber-300 text-sm font-medium hover:bg-amber-100">
                        {stats.pending_coaches} מאמנים ממתינים לאישור ←
                      </Link>
                    )}
                    {stats.pending_cancellations > 0 && (
                      <Link to="/admin/cancellations" className="px-3 py-1.5 bg-white rounded-lg border border-amber-300 text-sm font-medium hover:bg-amber-100">
                        {stats.pending_cancellations} ביטולי הרשמה ←
                      </Link>
                    )}
                    {incompleteUsers.length > 0 && (
                      <button onClick={() => setTab('users')} className="px-3 py-1.5 bg-white rounded-lg border border-amber-300 text-sm font-medium hover:bg-amber-100">
                        {incompleteUsers.length} פרופילים לא מלאים ←
                      </button>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Advanced Tools Banner */}
            <Link to="/admin/advanced" data-testid="advanced-tools-link" className="block">
              <Card className="border-0 bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 text-white hover:shadow-2xl transition-all hover:scale-[1.01]">
                <CardContent className="p-5 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center text-3xl">🛠️</div>
                    <div>
                      <h3 className="text-xl font-black">כלי ניהול מתקדמים</h3>
                      <p className="text-sm text-white/90">אנליטיקה · תבניות · שכפול · נאמנות · WhatsApp · גלריה · התראות חכמות</p>
                    </div>
                  </div>
                  <ChevronRight className="w-6 h-6 rotate-180" />
                </CardContent>
              </Card>
            </Link>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Registrations chart */}
              <Card className="lg:col-span-2">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2"><BarChart3 className="w-5 h-5 text-blue-600" />הרשמות חדשות - 30 ימים אחרונים</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-end gap-1 h-40">
                    {chart.map((d, i) => (
                      <div key={i} className="flex-1 group relative" title={`${d.date}: ${d.count} הרשמות`}>
                        <div
                          className="bg-gradient-to-t from-blue-500 to-cyan-400 rounded-t hover:from-blue-600 hover:to-cyan-500 transition-all relative"
                          style={{ height: `${(d.count / maxCount) * 100}%`, minHeight: d.count > 0 ? '4px' : '2px' }}
                        >
                          {d.count > 0 && (
                            <div className="absolute -top-6 left-1/2 -translate-x-1/2 bg-slate-900 text-white text-xs px-1.5 py-0.5 rounded opacity-0 group-hover:opacity-100 transition pointer-events-none">
                              {d.count}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-3 flex items-center justify-between text-xs text-slate-500">
                    <span>{chart[0]?.date}</span>
                    <span className="font-bold text-blue-600">סה"כ {stats.new_users_this_month || 0} הרשמות חודש</span>
                    <span>{chart[chart.length - 1]?.date}</span>
                  </div>
                </CardContent>
              </Card>

              {/* Recent registrations */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2"><UserPlus className="w-5 h-5 text-emerald-600" />הצטרפו לאחרונה</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {(dashboard?.recent_users || []).map(u => (
                    <Link key={u.id} to={`/profile/${u.id}`} className="flex items-center gap-2 p-2 rounded-lg hover:bg-slate-50">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center text-white text-xs font-bold">
                        {(u.full_name || '?').charAt(0)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-bold text-sm truncate">{u.full_name}</div>
                        <div className="text-xs text-slate-500">{u.skill_level || 'ללא רמה'}</div>
                      </div>
                      <ChevronRight className="w-4 h-4 text-slate-400" />
                    </Link>
                  ))}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* ============== TAB: Users ============== */}
          <TabsContent value="users" className="mt-6 space-y-6">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between gap-4 flex-wrap">
                  <CardTitle>כל המשתמשים ({allUsers.length})</CardTitle>
                  <div className="relative">
                    <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      placeholder="חיפוש לפי שם או אימייל..."
                      value={usersSearch}
                      onChange={(e) => setUsersSearch(e.target.value)}
                      className="w-64 pr-9"
                      data-testid="users-search-input"
                    />
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="text-right p-2 font-bold text-slate-700">שם</th>
                        <th className="text-right p-2 font-bold text-slate-700">אימייל</th>
                        <th className="text-right p-2 font-bold text-slate-700">רמה</th>
                        <th className="text-right p-2 font-bold text-slate-700">נקודות</th>
                        <th className="text-right p-2 font-bold text-slate-700">תחרויות</th>
                        <th className="text-right p-2 font-bold text-slate-700"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredUsers.slice(0, 100).map(u => (
                        <tr key={u.id} className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="p-2 font-medium">{u.full_name}</td>
                          <td className="p-2 text-slate-600 text-xs">{u.email}</td>
                          <td className="p-2">
                            {u.skill_level ? (
                              <span className="px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 text-xs font-bold">{u.skill_level}</span>
                            ) : (
                              <span className="text-amber-600 text-xs">חסר</span>
                            )}
                          </td>
                          <td className="p-2">{u.total_points || 0}</td>
                          <td className="p-2">{u.total_tournaments || 0}</td>
                          <td className="p-2">
                            <Link to={`/profile/${u.id}`}>
                              <Button size="sm" variant="outline" className="h-7 text-xs">
                                <Eye className="w-3 h-3 ml-1" />צפה
                              </Button>
                            </Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {filteredUsers.length > 100 && (
                    <div className="text-center text-xs text-slate-500 mt-3">מציג 100 מתוך {filteredUsers.length} - השתמש בחיפוש</div>
                  )}
                </div>
              </CardContent>
            </Card>

            {incompleteUsers.length > 0 && (
              <Card className="border-amber-200">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-amber-700"><AlertTriangle className="w-5 h-5" />פרופילים לא מלאים ({incompleteUsers.length})</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-96 overflow-y-auto">
                    {incompleteUsers.map(u => (
                      <Link key={u.id} to={`/profile/${u.id}`} className="flex items-center gap-2 p-2 rounded-lg bg-amber-50 hover:bg-amber-100 border border-amber-100">
                        <div className="flex-1 min-w-0">
                          <div className="font-bold text-sm truncate">{u.full_name}</div>
                          <div className="text-xs text-amber-700">חסר: {(u.missing || []).join(', ')}</div>
                        </div>
                        <ChevronRight className="w-4 h-4 text-amber-400" />
                      </Link>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* ============== TAB: Activity ============== */}
          <TabsContent value="activity" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><Activity className="w-5 h-5 text-purple-600" />יומן פעילות</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 max-h-[600px] overflow-y-auto">
                  {activity.length === 0 ? (
                    <p className="text-center text-slate-500 py-8">אין פעילות אחרונה</p>
                  ) : (
                    activity.map((a, i) => (
                      <div key={i} className="flex items-center gap-3 p-3 rounded-xl bg-slate-50 hover:bg-slate-100 transition-all" data-testid={`activity-item-${i}`}>
                        <ActivityIcon type={a.type} />
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-slate-900 truncate">{a.title}</p>
                          <p className="text-xs text-slate-500">{a.timestamp ? new Date(a.timestamp).toLocaleString('he-IL') : ''}</p>
                        </div>
                        {a.user_id && (
                          <Link to={`/profile/${a.user_id}`} className="text-blue-600 text-xs font-medium">צפה ←</Link>
                        )}
                        {a.tournament_id && (
                          <Link to={`/tournament/${a.tournament_id}`} className="text-amber-600 text-xs font-medium">צפה ←</Link>
                        )}
                        {a.coach_id && (
                          <Link to="/admin/coaches" className="text-purple-600 text-xs font-medium">צפה ←</Link>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ============== TAB: Tournaments ============== */}
          <TabsContent value="tournaments" className="mt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <Link to="/create-tournament">
                <Card className="border-emerald-200 cursor-pointer hover:shadow-xl hover:-translate-y-1 transition-all bg-gradient-to-br from-emerald-50 to-white">
                  <CardContent className="p-6 text-center">
                    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center mx-auto mb-3 shadow-lg">
                      <UserPlus className="w-7 h-7 text-white" />
                    </div>
                    <h3 className="font-black text-lg mb-1">צור תחרות חדשה</h3>
                    <p className="text-sm text-slate-600">יומית, נוקאאוט, או זוגות</p>
                  </CardContent>
                </Card>
              </Link>
              <Link to="/tournaments/daily">
                <Card className="border-blue-200 cursor-pointer hover:shadow-xl hover:-translate-y-1 transition-all bg-gradient-to-br from-blue-50 to-white">
                  <CardContent className="p-6 text-center">
                    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center mx-auto mb-3 shadow-lg">
                      <Calendar className="w-7 h-7 text-white" />
                    </div>
                    <h3 className="font-black text-lg mb-1">תחרויות יומיות</h3>
                    <p className="text-sm text-slate-600">{stats.active_tournaments} פעילות</p>
                  </CardContent>
                </Card>
              </Link>
              <Link to="/tournaments/knockout">
                <Card className="border-amber-200 cursor-pointer hover:shadow-xl hover:-translate-y-1 transition-all bg-gradient-to-br from-amber-50 to-white">
                  <CardContent className="p-6 text-center">
                    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center mx-auto mb-3 shadow-lg">
                      <Trophy className="w-7 h-7 text-white" />
                    </div>
                    <h3 className="font-black text-lg mb-1">תחרויות נוקאאוט</h3>
                    <p className="text-sm text-slate-600">{stats.completed_tournaments} הסתיימו</p>
                  </CardContent>
                </Card>
              </Link>
            </div>
          </TabsContent>

          {/* ============== TAB: Messages / Broadcast ============== */}
          <TabsContent value="messages" className="mt-6 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><Megaphone className="w-5 h-5 text-pink-600" />שליחת הודעה לכל המשתמשים</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Input
                  placeholder="כותרת ההודעה"
                  value={broadcastForm.title}
                  onChange={(e) => setBroadcastForm({ ...broadcastForm, title: e.target.value })}
                  data-testid="broadcast-title-input"
                />
                <textarea
                  placeholder="תוכן ההודעה..."
                  value={broadcastForm.body}
                  onChange={(e) => setBroadcastForm({ ...broadcastForm, body: e.target.value })}
                  className="w-full p-3 border rounded-lg min-h-[120px] text-sm"
                  data-testid="broadcast-body-input"
                />
                <Button
                  onClick={handleBroadcast}
                  className="bg-gradient-to-r from-pink-600 to-rose-500 hover:from-pink-700 hover:to-rose-600 text-white w-full"
                  data-testid="broadcast-send-btn"
                >
                  <Send className="w-4 h-4 ml-2" />
                  שלח ל-{stats.total_users || 0} משתמשים
                </Button>
              </CardContent>
            </Card>

            {/* Push Notification Broadcast */}
            <Card className="border-2 border-indigo-200" data-testid="push-broadcast-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Bell className="w-5 h-5 text-indigo-600" />
                  שליחת התראת פוש לכל המשתמשים
                </CardTitle>
                <p className="text-sm text-slate-500 mt-1">
                  ההתראה תקפוץ על המכשירים של כל מי שהפעיל התראות (גם כשהאתר סגור) ותופיע בפעמון ההתראות באתר.
                  {pushStats != null && (
                    <span className="block mt-1 font-semibold text-indigo-600" data-testid="push-stats-text">
                      📱 {pushStats.device_subscriptions} מכשירים רשומים · {pushStats.subscribed_users} משתמשים
                    </span>
                  )}
                </p>
              </CardHeader>
              <CardContent className="space-y-3">
                <Input
                  placeholder="כותרת ההתראה (לדוגמה: נפתחה הרשמה לתחרות חדשה!)"
                  value={pushForm.title}
                  onChange={(e) => setPushForm({ ...pushForm, title: e.target.value })}
                  data-testid="push-title-input"
                />
                <textarea
                  placeholder="תוכן ההתראה..."
                  value={pushForm.body}
                  onChange={(e) => setPushForm({ ...pushForm, body: e.target.value })}
                  className="w-full p-3 border rounded-lg min-h-[100px] text-sm"
                  data-testid="push-body-input"
                />
                <Input
                  placeholder="קישור אופציונלי (לדוגמה: /tournaments/daily)"
                  value={pushForm.link}
                  onChange={(e) => setPushForm({ ...pushForm, link: e.target.value })}
                  data-testid="push-link-input"
                />

                {/* Target audience selector */}
                <div className="pt-1">
                  <label className="block text-sm font-bold text-slate-700 mb-2">למי לשלוח?</label>
                  <div className="grid grid-cols-3 gap-2" data-testid="push-target-selector">
                    {[
                      { v: 'all', l: 'כל המשתמשים' },
                      { v: 'skill_level', l: 'לפי דרג' },
                      { v: 'tournament', l: 'נרשמי תחרות' },
                    ].map((opt) => (
                      <button
                        key={opt.v}
                        type="button"
                        onClick={() => setPushForm({ ...pushForm, target: opt.v })}
                        data-testid={`push-target-${opt.v}`}
                        className={`px-2 py-2 rounded-lg text-sm font-semibold border transition-all ${pushForm.target === opt.v ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-slate-600 border-slate-200 hover:border-indigo-300'}`}
                      >
                        {opt.l}
                      </button>
                    ))}
                  </div>

                  {pushForm.target === 'skill_level' && (
                    <select
                      value={pushForm.skill_level}
                      onChange={(e) => setPushForm({ ...pushForm, skill_level: e.target.value })}
                      className="w-full mt-2 p-2.5 border rounded-lg text-sm bg-white"
                      data-testid="push-skill-select"
                    >
                      <option value="">בחר דרג...</option>
                      {(pushStats?.skill_levels || []).map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  )}

                  {pushForm.target === 'tournament' && (
                    <div className="mt-2 space-y-2">
                      <select
                        value={pushForm.tournament_id}
                        onChange={(e) => setPushForm({ ...pushForm, tournament_id: e.target.value })}
                        className="w-full p-2.5 border rounded-lg text-sm bg-white"
                        data-testid="push-tournament-select"
                      >
                        <option value="">בחר תחרות...</option>
                        {(pushStats?.tournaments || []).map((t) => (
                          <option key={t.id} value={t.id}>{t.name}{t.date ? ` · ${t.date}` : ''}</option>
                        ))}
                      </select>
                      <select
                        value={pushForm.tournament_audience}
                        onChange={(e) => setPushForm({ ...pushForm, tournament_audience: e.target.value })}
                        className="w-full p-2.5 border rounded-lg text-sm bg-white"
                        data-testid="push-tournament-audience"
                      >
                        <option value="registered">רק נרשמים</option>
                        <option value="waiting">רק רשימת המתנה</option>
                        <option value="all">נרשמים + רשימת המתנה</option>
                      </select>
                    </div>
                  )}
                </div>

                <Button
                  onClick={handleSendPush}
                  disabled={sendingPush}
                  className="bg-gradient-to-r from-indigo-600 to-blue-500 hover:from-indigo-700 hover:to-blue-600 text-white w-full"
                  data-testid="push-send-btn"
                >
                  <Bell className="w-4 h-4 ml-2" />
                  {sendingPush ? 'שולח...' : (pushForm.target === 'all' ? 'שלח התראת פוש לכולם' : 'שלח התראת פוש')}
                </Button>
              </CardContent>
            </Card>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Link to="/admin/cancellations">
                <Card className="border-red-200 hover:shadow-xl hover:-translate-y-1 transition-all bg-gradient-to-br from-red-50 to-white">
                  <CardContent className="p-6 text-center">
                    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-red-500 to-pink-500 flex items-center justify-center mx-auto mb-3 shadow-lg">
                      <MessageSquare className="w-7 h-7 text-white" />
                    </div>
                    <h3 className="font-black text-lg mb-1">ביטולי הרשמה</h3>
                    <p className="text-sm text-slate-600">{stats.pending_cancellations || 0} ביטולים</p>
                  </CardContent>
                </Card>
              </Link>
              <Link to="/admin/coaches">
                <Card className="border-purple-200 hover:shadow-xl hover:-translate-y-1 transition-all bg-gradient-to-br from-purple-50 to-white">
                  <CardContent className="p-6 text-center">
                    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-purple-500 to-indigo-500 flex items-center justify-center mx-auto mb-3 shadow-lg">
                      <UserCheck className="w-7 h-7 text-white" />
                    </div>
                    <h3 className="font-black text-lg mb-1">בקשות מאמנים</h3>
                    <p className="text-sm text-slate-600">{stats.pending_coaches || 0} ממתינים</p>
                  </CardContent>
                </Card>
              </Link>
            </div>
          </TabsContent>

          {/* ============== TAB: Tools ============== */}
          <TabsContent value="tools" className="mt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-purple-600"><RefreshCw className="w-5 h-5" />סנכרון סטטיסטיקות</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-slate-600 mb-4 text-sm">חישוב מחדש של ניצחונות/הפסדים/תחרויות לכל השחקנים מהנתונים בפועל. הניקוד לא ישתנה.</p>
                  <Button onClick={handleRecompute} disabled={tools.recompute} className="w-full bg-purple-600 hover:bg-purple-700 text-white" data-testid="tool-recompute-btn">
                    {tools.recompute ? 'מחשב...' : 'חשב מחדש'}
                  </Button>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-indigo-600"><CheckCircle2 className="w-5 h-5" />סגירת תחרויות</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-slate-600 mb-4 text-sm">סימון תחרויות שמשחק הגמר שלהן הסתיים כ"הסתיים" וסנכרון סטטיסטיקות.</p>
                  <Button onClick={handleAutoComplete} disabled={tools.autoComplete} className="w-full bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="tool-auto-complete-btn">
                    {tools.autoComplete ? 'מסמן...' : 'סגור תחרויות'}
                  </Button>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminControlPanel;
